import type { FC, ReactNode } from "react";

interface ReadOnlyBulletedListProps {
  items: string[];
  maxItemsToDisplay?: number;
  itemDescriptor?: string;
  icon?: ReactNode | ((item: string, index: number) => ReactNode);
}

const defaultIcon = "•";

const renderIcon = (
  icon: ReactNode | ((item: string, index: number) => ReactNode) | undefined,
  item: string,
  index: number,
): ReactNode => {
  const providedIcon = typeof icon === "function" ? icon(item, index) : icon;

  return providedIcon || defaultIcon;
};

export const ReadOnlyBulletedList: FC<ReadOnlyBulletedListProps> = ({
  items,
  maxItemsToDisplay,
  itemDescriptor = "items",
  icon,
}) => {
  const shouldTruncate =
    maxItemsToDisplay && items.length > maxItemsToDisplay + 1;
  const displayItems = shouldTruncate
    ? items.slice(0, maxItemsToDisplay)
    : items;
  const excessItems = items.length - (maxItemsToDisplay || 0);

  return (
    <div className="flex flex-auto flex-col divide-y divide-border-light text-sm">
      {displayItems.map((item, index) => (
        <div
          key={index}
          className="flex items-center justify-between px-3 py-2"
        >
          <div className="flex items-center">
            <span className="mr-2 text-muted-foreground">
              {renderIcon(icon, item, index)}
            </span>
            <span>{item}</span>
          </div>
        </div>
      ))}
      {shouldTruncate ? (
        <div className="flex items-center justify-center px-3 py-2 text-sm text-blue-500">
          +{excessItems} more {itemDescriptor}
        </div>
      ) : null}
    </div>
  );
};
